import React, { useState, useRef, useEffect } from 'react'
import {
  getCopilotSkill,
  uploadCopilotSkill,
  getErrorMessage,
  type CopilotSkillInfo,
  type CopilotSkillUploadResult,
} from '../../utils/api'
import { PrimaryButton, OutlineButton, Spinner } from '../ui'

interface UpdateSkillDialogProps {
  onClose: () => void
  onUploaded: (result: CopilotSkillUploadResult) => void
}

/**
 * Modal for replacing the Copilot's knowledge skill. Admin/supervisor only —
 * gating is enforced by the caller (the button isn't rendered otherwise) and
 * again server-side. Accepts a .skill / .zip archive, POSTs it multipart, and
 * reports the resulting diff.
 */
export const UpdateSkillDialog: React.FC<UpdateSkillDialogProps> = ({ onClose, onUploaded }) => {
  const [info, setInfo] = useState<CopilotSkillInfo | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !uploading) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, uploading])

  // Fetch current skill metadata (best-effort).
  useEffect(() => {
    let active = true
    getCopilotSkill()
      .then((d) => active && setInfo(d))
      .catch(() => {
        /* no skill installed yet — fine */
      })
    return () => {
      active = false
    }
  }, [])

  const submit = async () => {
    if (!file || uploading) return
    setUploading(true)
    setError(null)
    setProgress(0)
    try {
      const result = await uploadCopilotSkill(file, setProgress)
      onUploaded(result)
      onClose()
    } catch (err) {
      setError(getErrorMessage(err, 'Upload failed'))
      setUploading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Update Copilot skill"
      onClick={() => !uploading && onClose()}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface-elevated border border-border shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-txt-primary">Update Copilot knowledge</h2>
        <p className="text-sm text-txt-secondary mt-1">
          Upload a new skill archive (.skill or .zip) to refresh the knowledge base
          the Copilot grounds its answers in.
        </p>

        {info && (
          <div className="mt-3 text-xs text-txt-tertiary">
            Current: v{info.version} · {info.article_count} article
            {info.article_count === 1 ? '' : 's'} · {info.filename}
          </div>
        )}

        <div className="mt-4">
          <input
            ref={inputRef}
            type="file"
            accept=".skill,.zip,application/zip"
            disabled={uploading}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-border hover:border-brand-400 hover:bg-surface-hover transition-colors text-txt-secondary disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <span className="text-sm">
              {file ? file.name : 'Choose a .skill / .zip file'}
            </span>
          </button>
        </div>

        {uploading && (
          <div className="mt-4">
            <div className="h-2 w-full rounded-full bg-surface-tertiary overflow-hidden">
              <div
                className="h-full bg-brand-600 transition-all duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-1 text-xs text-txt-tertiary flex items-center gap-2">
              <Spinner size="sm" />
              Uploading… {progress}%
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 text-sm text-error">{error}</div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <OutlineButton onClick={onClose} disabled={uploading}>
            Cancel
          </OutlineButton>
          <PrimaryButton onClick={submit} disabled={!file} loading={uploading}>
            Upload &amp; update
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}
